import React, { useMemo } from "react";
import { Box, Button, Tab, TabProps, Tabs, alpha, styled, useMediaQuery } from "@mui/material";
import { AutoAwesome, ChatBubble, LocalMall, Stars, TextSnippet } from "@mui/icons-material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SettingsIcon from "@mui/icons-material/Settings";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { Chat } from "./chat";
import { Shop } from "./shop";
import { Read } from "./read";
import { Ai } from "./ai";
import { Rewards } from "./rewards";
import { ChatFont } from "./ChatFont";
import { useImmersivePanelContext } from "./ImmersivePanelContext";
import { userSelector } from "../../store/auth/selector";
import { Guest } from "./guest";
import { useEntityLayoutContext } from "../../layouts/entity-layout/EntityLayoutContext";
import { useImmersivePanelLayoutContext } from "../../layouts";
import { streamDataSelector } from "../../store/streamer/selector";
import { StreamSummaryBanner } from "./StreamSummaryBanner";

const AI_API_URL = process.env.REACT_APP_AI_API_URL;
if (!AI_API_URL) {
  throw new Error("REACT_APP_AI_API_URL is not defined");
}
const WS_BASE = AI_API_URL.replace(/^http/, "ws");

export const ImmersivePanelUi = () => {
  const { activeTab, chatProps, updateContext: updateImmersivePanelContext } = useImmersivePanelContext();
  const user = useSelector(userSelector);

  // summary vars
  const { url: hlsUrl } = useSelector(streamDataSelector);
  const [summary, setSummary] = React.useState<string | null>(null);
  const [showBanner, setShowBanner] = React.useState(false);

  /** Poll `/get-stream-summary` every 60 s while we have a URL */
  React.useEffect(() => {
    console.log("US URLS: ", WS_BASE);
    if (!hlsUrl) {
      return undefined; // <- satisfy ESLint
    }

    // url-encode because the manifest contains slashes & ?
    // const encoded = encodeURIComponent(hlsUrl);
    const ws = new WebSocket(`${WS_BASE}/ws/summary/${hlsUrl}`);

    ws.onopen = () => console.log("summary-ws open");

    ws.onmessage = (evt) => {
      try {
        const { summary: text } = JSON.parse(evt.data);
        if (text && text !== summary) {
          console.log("Received summary: ", text);
          setSummary(text);
          setShowBanner(true);
        }
      } catch (e) {
        /* ignore malformed */
      }
    };

    ws.onerror = (e) => console.error("summary-ws error", e);
    ws.onclose = () => console.log("summary-ws closed");

    return () => ws.close(); // cleanup on unmount / url change
  }, [hlsUrl]); // re-run if the stream changes

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    updateImmersivePanelContext({ activeTab: newValue });
  };

  const isProd = process.env.REACT_APP_APP_ENV === "prod";
  const { entity } = useEntityLayoutContext();

  const visibleTabs = useMemo(() => {
    // Memoize visible tabs
    return [
      entity?.data?.enableChat && { label: "Chat", component: <Chat {...chatProps} />, icon: <ChatBubble /> },
      entity?.data?.enableShop && !isProd && { label: "Shop", component: <Shop />, icon: <LocalMall /> },
      entity?.data?.enableRead && !isProd && { label: "Read", component: <Read />, icon: <TextSnippet /> },
      entity?.data?.enableAI && { label: "AI", component: <Ai />, icon: <AutoAwesome /> },
      entity?.data?.enableReward && !isProd && { label: "Rewards", component: <Rewards />, icon: <Stars /> },
      entity?.data?.enableChat && !isProd && { label: "Settings", component: <ChatFont />, icon: <SettingsIcon /> },
    ].filter(Boolean); // Filter out null/false entries
  }, [entity, isProd, chatProps]);

  const isSm = useMediaQuery("(max-width:768px)");
  const { immersivePanel__open, updateContext: updateImmersivePanelLayout } = useImmersivePanelLayoutContext();

  return (
    <Box
      sx={{ width: "100%", display: "flex", flexDirection: "column", flex: 1, position: "relative" }}
      className={user?.username === "guest" ? "guest_boxWrapper" : ""}
    >
      <Box
        sx={{
          py: 2,
          width: "100%",
          borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: alpha("#13162C", 0.6),
          backdropFilter: "blur(40px)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: "unset",

            ".MuiTabs-hideScrollbar": {
              top: `0px !important`,
            },

            ".MuiTabs-flexContainer": {
              display: "block",
            },

            ".MuiTabs-scrollButtons": {
              position: "absolute",
              zIndex: 1,
              width: 25,
              height: 25,
              borderRadius: 25,
              bgcolor: "#3C436F",
              opacity: 1,
              top: "50%",
              transform: "translateY(-50%)",

              "&:first-child": {
                left: 12,
              },

              "&:last-child": {
                right: 12,
              },

              "&.MuiTabScrollButton-root.Mui-disabled": {
                opacity: 0,
              },

              "@media (max-width: 599.95px)": {
                display: "inline-flex",
              },
            },

            ".MuiTab-root": {
              mx: 1,

              "&:first-child": {
                ml: 2,
              },

              "&:last-child": {
                mr: 4,
              },
            },

            ".MuiTabs-indicator": {
              display: "none",
            },
          }}
        >
          {visibleTabs.map((tab, index) => (
            <CustomTab key={index} label={tab.label} icon={tab.icon} />
          ))}
        </Tabs>

        {/* <Box sx={{ width: "calc(100% - 80px)", position: "relative" }}>
          
        </Box> */}

        {/* {immersivePanel__open && isSm && (
          <Button
            variant="contained"
            sx={{
              minWidth: 0,
              p: 0.8,
              mr: 2.5,
              backgroundColor: "transparent",
              border: "1px solid #fff",
              borderRadius: "100%",
            }}
            onClick={() => {
              updateImmersivePanelLayout({
                immersivePanel__open: false,
              });
            }}
          >
            <CloseIcon />
          </Button>
        )} */}

        {/* ------- summary banner just under the tabs ------------------ */}
        {showBanner && summary && (
          <StreamSummaryBanner
            summary={summary}
            hlsUrl={hlsUrl || ''}
            onClose={() => setShowBanner(false)}
            sx={{
              position: "absolute",
              top: "100%", // flush under the tab bar
              left: 0,
              right: 0,
              mt: 1, // (optional) little gap
              zIndex: 3,
            }}
          />
        )}

        <Box
          sx={{
            position: "absolute",
            top: 0,
            zIndex: 10,
            height: "100%",
            width: 24,
            background: "linear-gradient(90deg, rgba(19,22,44,1) 0%, rgba(19,22,44,0.5) 39%, rgba(255,255,255,0) 100%)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            zIndex: 10,
            height: "100%",
            width: 24,
            background:
              "linear-gradient(270deg, rgba(19,22,44,1) 0%, rgba(19,22,44,0.5) 39%, rgba(255,255,255,0) 100%)",
            pointerEvents: "none",
          }}
        />
      </Box>

      {visibleTabs.length > 0 && visibleTabs[activeTab]?.component}

      {user?.username === "guest" && <Guest />}
    </Box>
  );
};

const CustomTab = styled((props: TabProps) => <Tab {...props} />)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  color: "#fff",
  paddingLeft: 12,
  paddingRight: 12,
  paddingTop: 4,
  paddingBottom: 4,
  borderRadius: 6,
  minHeight: 38,
  minWidth: 0,
  flexDirection: "row",
  alignItems: "center",
  columnGap: 8,

  [theme.breakpoints.up("sm")]: {
    minWidth: 0,
  },

  "&:hover": {
    opacity: 1,
  },

  "&.Mui-selected": {
    backgroundColor: "#FFFFFF",
    color: "#161A2F",
  },

  ".MuiTab-iconWrapper": {
    marginBottom: 0,
  },
}));
